import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/editoriales")({
  head: () => ({
    meta: [
      { title: "Admin — Editoriales | Mine Class" },
      { name: "description", content: "Panel de administración de Editoriales — acceso restringido a Mine Class." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EditorialesAdmin,
});

type Editorial = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  author: string | null;
  body_html: string | null;
  cover_image_url: string | null;
  is_published: boolean;
  published_at: string | null;
  sort_order: number;
};

const empty: Partial<Editorial> = {
  slug: "",
  title: "",
  subtitle: "",
  author: "",
  body_html: "",
  cover_image_url: "",
  is_published: false,
  sort_order: 0,
};

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

function EditorialesAdmin() {
  const [items, setItems] = useState<Editorial[]>([]);
  const [editing, setEditing] = useState<Partial<Editorial> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    const { data } = await supabase
      .from("editoriales")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("published_at", { ascending: false, nullsFirst: false });
    setItems((data ?? []) as Editorial[]);
  }
  useEffect(() => {
    load();
  }, []);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setBusy(true);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const path = `${userId}/editoriales/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error } = await supabase.storage.from("media").upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data } = await supabase.storage.from("media").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      setEditing({ ...editing, cover_image_url: data?.signedUrl ?? "" });
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Error");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!editing) return;
    setErr(null);
    const payload = {
      slug: editing.slug || slugify(editing.title ?? ""),
      title: editing.title ?? "",
      subtitle: editing.subtitle || null,
      author: editing.author || null,
      body_html: editing.body_html || null,
      cover_image_url: editing.cover_image_url || null,
      is_published: editing.is_published ?? false,
      published_at: editing.is_published ? (editing.published_at ?? new Date().toISOString()) : editing.published_at ?? null,
      sort_order: editing.sort_order ?? 0,
    };
    const q = editing.id
      ? supabase.from("editoriales").update(payload).eq("id", editing.id)
      : supabase.from("editoriales").insert(payload);
    const { error } = await q;
    if (error) {
      setErr(error.message);
      return;
    }
    setEditing(null);
    setPreview(false);
    load();
  }

  async function togglePublished(e: Editorial) {
    await supabase
      .from("editoriales")
      .update({
        is_published: !e.is_published,
        published_at: !e.is_published ? (e.published_at ?? new Date().toISOString()) : e.published_at,
      })
      .eq("id", e.id);
    load();
  }

  async function move(e: Editorial, dir: -1 | 1) {
    const idx = items.findIndex((i) => i.id === e.id);
    const other = items[idx + dir];
    if (!other) return;
    await Promise.all([
      supabase.from("editoriales").update({ sort_order: other.sort_order }).eq("id", e.id),
      supabase.from("editoriales").update({ sort_order: e.sort_order }).eq("id", other.id),
    ]);
    load();
  }

  async function remove(e: Editorial) {
    if (!confirm(`Eliminar editorial "${e.title}"?`)) return;
    await supabase.from("editoriales").delete().eq("id", e.id);
    load();
  }

  if (editing) {
    return (
      <div className="bg-white border border-[var(--color-outline-variant)] p-6 space-y-4 max-w-4xl">
        <h2 className="font-bold text-lg">{editing.id ? "Editar" : "Nueva"} editorial</h2>
        <Field label="Título">
          <input
            value={editing.title ?? ""}
            onChange={(ev) =>
              setEditing({ ...editing, title: ev.target.value, slug: editing.id ? editing.slug : slugify(ev.target.value) })
            }
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>
        <Field label="Slug (URL)">
          <input
            value={editing.slug ?? ""}
            onChange={(ev) => setEditing({ ...editing, slug: slugify(ev.target.value) })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2 font-mono text-sm"
          />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Bajada / subtítulo">
            <input
              value={editing.subtitle ?? ""}
              onChange={(ev) => setEditing({ ...editing, subtitle: ev.target.value })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
            />
          </Field>
          <Field label="Autor">
            <input
              value={editing.author ?? ""}
              onChange={(ev) => setEditing({ ...editing, author: ev.target.value })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
              placeholder="Claudio Valenzuela, CEO de Mine Class"
            />
          </Field>
        </div>
        <Field label="Imagen de portada (para el listado / redes)">
          <div className="space-y-2">
            {editing.cover_image_url && (
              <img src={editing.cover_image_url} alt="" className="w-full max-w-md aspect-video object-cover" />
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} disabled={busy} className="text-sm" />
          </div>
        </Field>
        <Field label="HTML de la editorial (pega aquí el código completo, incluido el <style>)">
          <textarea
            value={editing.body_html ?? ""}
            onChange={(ev) => setEditing({ ...editing, body_html: ev.target.value })}
            rows={20}
            spellCheck={false}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2 font-mono text-xs"
            placeholder="<style> ... </style> <div class='mc-land'> ... </div>"
          />
        </Field>
        <div className="flex items-center gap-4 flex-wrap">
          <button
            type="button"
            onClick={() => setPreview((p) => !p)}
            className="px-3 py-2 border border-[var(--color-outline-variant)] text-sm"
          >
            {preview ? "Ocultar" : "Ver"} vista previa
          </button>
          <label className="flex items-center gap-2 text-sm">
            Orden
            <input
              type="number"
              value={editing.sort_order ?? 0}
              onChange={(ev) => setEditing({ ...editing, sort_order: Number(ev.target.value) })}
              className="w-20 border border-[var(--color-outline-variant)] px-2 py-1"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={editing.is_published ?? false}
              onChange={(ev) => setEditing({ ...editing, is_published: ev.target.checked })}
            />
            Publicado (desmarcar para dar de baja del sitio)
          </label>
        </div>
        {preview && editing.body_html && (
          <div className="border border-[var(--color-outline-variant)] bg-white overflow-hidden">
            <iframe title="Vista previa editorial" srcDoc={editing.body_html} className="w-full h-[600px] border-0" />
          </div>
        )}
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">
            Guardar
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setPreview(false);
            }}
            className="px-4 py-2 border border-[var(--color-outline-variant)]"
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="font-bold text-lg">Editoriales ({items.length})</h2>
        <button
          onClick={() => setEditing({ ...empty })}
          className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold"
        >
          + Nueva editorial
        </button>
      </div>
      <div className="bg-white border border-[var(--color-outline-variant)] overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[var(--color-surface-container-low)] text-left">
            <tr>
              <th className="px-4 py-3">Título</th>
              <th className="px-4 py-3">Autor</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Orden</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-[var(--color-outline-variant)]">
                <td className="px-4 py-3 font-semibold">{e.title}</td>
                <td className="px-4 py-3 text-xs">{e.author ?? "—"}</td>
                <td className="px-4 py-3 text-xs">
                  {e.is_published ? (
                    <span className="text-green-700 font-bold">Publicada</span>
                  ) : (
                    <span className="text-[var(--color-on-surface-variant)]">Borrador</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs whitespace-nowrap">
                  <button onClick={() => move(e, -1)} className="px-1">▲</button>
                  <button onClick={() => move(e, 1)} className="px-1">▼</button>
                </td>
                <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                  <a
                    href={`/comunicaciones/editoriales/${e.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs underline"
                  >
                    Ver
                  </a>
                  <button onClick={() => setEditing(e)} className="text-xs underline">
                    Editar
                  </button>
                  <button onClick={() => togglePublished(e)} className="text-xs underline">
                    {e.is_published ? "Dar de baja" : "Publicar"}
                  </button>
                  <button onClick={() => remove(e)} className="text-xs text-red-600">
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-[var(--color-on-surface-variant)]">
                  Sin editoriales aún.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
